// Configuración de rutas de la aplicación con protección de acceso
import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import useAuthStore from '../store/useAuthStore'
import Layout from '../components/layout/Layout'

// Páginas públicas
import Landing from '../pages/Landing'
import Login from '../pages/Login'
import Register from '../pages/Register'
import WorldCupMatches from '../pages/WorldCupMatches'
import WorldCupStandings from '../pages/WorldCupStandings'
import Rules from '../pages/Rules'
import TermsOfUse from '../pages/TermsOfUse'
import PrivacyPolicy from '../pages/PrivacyPolicy'
import DMCAPolicy from '../pages/DMCAPolicy'

// Páginas protegidas (requieren autenticación)
import Dashboard from '../pages/Dashboard'
import PredictionsWizard from '../pages/PredictionsWizard'
import Leaderboard from '../pages/Leaderboard'
import Communities from '../pages/Communities'
import Stats from '../pages/Stats'
import AdminPanel from '../pages/AdminPanel'
import BettingSection from '../pages/BettingSection'
import TransactionsSection from '../pages/TransactionsSection'
import LiveStreaming from '../pages/LiveStreaming'
import Profile from '../pages/Profile'
import PersonalFinances from '../pages/PersonalFinances'
import WalletHistory from '../pages/WalletHistory'


// Componente que protege rutas que requieren autenticación
function ProtectedRoute({ children }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return children
}

// Componente que protege rutas de administrador
function AdminRoute({ children }) {
  const user = useAuthStore((state) => state.user)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  if (user?.rol === 'admin' || user?.rol === 'superadmin') {
    return children
  }

  // Verificar si tiene al menos un permiso administrativo habilitado
  const tienePermisosAdmin = user?.permisos && Object.values(user.permisos).some(val => val === 1)
  if (tienePermisosAdmin) {
    return children
  }

  return <Navigate to="/dashboard" replace />
}

// Componente que protege rutas de staff (empleado, admin, superadmin)
function StaffRoute({ children }) {
  const user = useAuthStore((state) => state.user)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  if (user?.rol !== 'admin' && user?.rol !== 'superadmin' && user?.rol !== 'empleado') {
    return <Navigate to="/dashboard" replace />
  }
  return children
}


// Redirige a dashboard si ya está autenticado
function GuestRoute({ children }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }
  return children
}

// Resetea el scroll al inicio en cada cambio de ruta
function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

export default function AppRouter() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const basename = window.__APP_BASE_URL__ ? window.__APP_BASE_URL__.replace(/\/$/, '') : ''
  return (
    <BrowserRouter basename={basename}>
      <ScrollToTop />
      <Routes>
        {/* Rutas públicas sin layout */}
        <Route
          path="/"
          element={
            <GuestRoute>
              <Landing />
            </GuestRoute>
          }
        />
        <Route
          path="/login"
          element={
            <GuestRoute>
              <Login />
            </GuestRoute>
          }
        />
        <Route
          path="/registro"
          element={
            <GuestRoute>
              <Register />
            </GuestRoute>
          }
        />

        {/* Rutas públicas con layout (partidos, clasificación, estadísticas, ranking, reglas, legal) */}
        <Route element={<Layout showSidebar={isAuthenticated} />}>
          <Route path="/partidos" element={<WorldCupMatches />} />
          <Route path="/clasificacion" element={<WorldCupStandings />} />
          <Route path="/estadisticas" element={<Stats />} />
          <Route path="/ranking" element={<Leaderboard />} />
          <Route path="/reglas" element={<Rules />} />
          <Route path="/terminos" element={<TermsOfUse />} />
          <Route path="/privacidad" element={<PrivacyPolicy />} />
          <Route path="/dmca" element={<DMCAPolicy />} />
        </Route>

        {/* Rutas protegidas con layout completo */}
        <Route
          element={
            <ProtectedRoute>
              <Layout showSidebar={true} />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/predicciones" element={<PredictionsWizard />} />
          <Route path="/apuestas" element={<BettingSection />} />
          <Route path="/comunidades" element={<Communities />} />
          <Route path="/movimientos" element={<TransactionsSection />} />
          <Route path="/historial-billetera" element={<WalletHistory />} />
          <Route path="/ver-partido" element={<LiveStreaming />} />
          <Route path="/perfil" element={<Profile />} />
        </Route>

        {/* Rutas de staff (empleado / admin) */}
        <Route
          element={
            <StaffRoute>
              <Layout showSidebar={true} />
            </StaffRoute>
          }
        >
          <Route path="/finanzas-personal" element={<PersonalFinances />} />
        </Route>

        {/* Rutas de administración */}
        <Route
          element={
            <AdminRoute>
              <Layout showSidebar={true} />
            </AdminRoute>
          }
        >
          <Route path="/admin" element={<AdminPanel />} />
        </Route>


        {/* Ruta por defecto: redirigir a la página principal */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
